import React, { useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import { X, Check, TrendingUp, TrendingDown, Filter } from 'lucide-react';


/**
 * Bulk Approval modal — applies the engine's suggested_priority to many
 * rules at once with admin-controlled filters:
 *  - min_delta:    minimum |delta| to qualify
 *  - min_events:   minimum total events the rule must have
 *  - min_success:  minimum success_rate
 *  - direction:    'up' | 'down' | 'any'
 * Optional explicit rule_ids[] override all filters.
 */
export default function BulkApprovalModal({ initialRuleIds = [], onClose, onApplied }) {
  const useExplicit = initialRuleIds.length > 0;
  const [busy, setBusy] = useState(false);
  const [minDelta, setMinDelta]     = useState(5);
  const [minEvents, setMinEvents]   = useState(3);
  const [minSuccess, setMinSuccess] = useState(0);    // percent
  const [direction, setDirection]   = useState('any');
  const [reason, setReason]         = useState('');
  const [result, setResult]         = useState(null);

  const apply = async () => {
    setBusy(true);
    try {
      const payload = useExplicit
        ? { rule_ids: initialRuleIds, reason: reason || undefined }
        : {
            min_delta: minDelta,
            min_events: minEvents,
            min_success: minSuccess / 100,
            direction,
            reason: reason || undefined,
          };
      const { data } = await axiosInstance.post(
        '/bank-rules-intel/bulk-approve-ranking', payload);
      setResult(data);
      toast.success(`تَم تَطبيق ${data.applied_count} قاعِدَة، تُجوُوِزَت ${data.skipped_count}`);
      onApplied?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل التَّطبيق');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="bulk-approve-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" dir="rtl">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-emerald-600" />
            <div className="text-base font-bold">اعتِماد جَماعي لِلتَّرتيب</div>
          </div>
          <button onClick={onClose} data-testid="bulk-close"
                  className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {useExplicit ? (
            <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3 text-sm">
              <div className="font-semibold text-indigo-900">
                {initialRuleIds.length} قاعِدَة مُحَدَّدَة يَدَوِيّاً.
              </div>
              <div className="text-xs text-indigo-700 mt-1">
                سيَتِم تَطبيق <code>_suggested_priority</code> على كُلٍّ مِنها.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Filter className="w-4 h-4" /> الفِلاتِر:
              </div>
              <div>
                <label className="text-xs text-slate-700 font-semibold flex items-center justify-between">
                  أَدنى فَرق (|Δ|): <span className="font-mono">{minDelta}</span>
                </label>
                <input type="range" min="0" max="50" value={minDelta}
                       onChange={(e) => setMinDelta(Number(e.target.value))}
                       data-testid="bulk-min-delta"
                       className="w-full mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-700 font-semibold flex items-center justify-between">
                  أَدنى عَدَد أَحداث: <span className="font-mono">{minEvents}</span>
                </label>
                <input type="range" min="0" max="100" value={minEvents}
                       onChange={(e) => setMinEvents(Number(e.target.value))}
                       data-testid="bulk-min-events"
                       className="w-full mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-700 font-semibold flex items-center justify-between">
                  أَدنى نِسبَة نَجاح: <span className="font-mono">{minSuccess}%</span>
                </label>
                <input type="range" min="0" max="100" value={minSuccess}
                       onChange={(e) => setMinSuccess(Number(e.target.value))}
                       data-testid="bulk-min-success"
                       className="w-full mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-700 font-semibold">الاتِّجاه:</label>
                <div className="flex items-center gap-1 mt-1">
                  {[
                    { id: 'any',  label: 'الكُل',  Icon: null },
                    { id: 'up',   label: 'رَفع', Icon: TrendingUp },
                    { id: 'down', label: 'خَفض', Icon: TrendingDown },
                  ].map((d) => {
                    const Ic = d.Icon;
                    return (
                      <button key={d.id} onClick={() => setDirection(d.id)}
                              data-testid={`bulk-direction-${d.id}`}
                              className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full border px-3 py-1 ${
                                direction === d.id ? 'bg-slate-900 text-white border-slate-900'
                                                   : 'bg-white border-slate-300 hover:bg-slate-50'}`}>
                        {Ic && <Ic className="w-3.5 h-3.5" />} {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-700 font-semibold">سَبَب (اختياري):</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="مثال: ضَبط الأَولَوِيّات الفَصلي"
                   data-testid="bulk-reason"
                   className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1" />
          </div>

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs"
                  data-testid="bulk-result">
              <div className="font-semibold text-emerald-900 mb-1">
                تَم: {result.applied_count} مُطَبَّقَة، {result.skipped_count} مَتَجاوَزَة.
              </div>
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {result.applied.map((a) => (
                  <div key={a.id} className="flex items-center justify-between font-mono text-emerald-800">
                    <span>{a.name}</span>
                    <span>{a.from} → {a.to} <span className="text-[10px] opacity-60">({a.delta > 0 ? '+' : ''}{a.delta})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose}
                  data-testid="bulk-cancel"
                  className="text-sm font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-100 px-4 py-2">
            إلغاء
          </button>
          <button onClick={apply} disabled={busy}
                  data-testid="bulk-apply"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 disabled:opacity-50">
            <Check className="w-4 h-4" /> {busy ? '…' : 'تَطبيق'}
          </button>
        </div>
      </div>
    </div>
  );
}
